import React from 'react';
import {
  Action, FormHandlers, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import UpdateApplicantForm, { UpdateApplicantFormModel } from '../../components/update-applicant-form/UpdateApplicantForm';
import { applicantToUpdateApplicantFormModel, updateApplicantFormModalToApplicant } from '../../components/update-applicant-form/mappers';
import { updateApplicant } from '../../../providers/api/applicant/applicantProvider';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Applicant } from '../../../providers/api/dtos/applicant';

export const APPLICANT_UPDATE_SUCCESS_MESSAGE = 'Il candidato è stato aggiornato con successo.';
export const APPLICANT_UPDATE_ERROR_MESSAGE = 'Non è stato possibile aggiornare il candidato.';

interface ChildFnProps {
  isSaving: boolean,
  openUpdateModal: VoidFunction,
}

export interface UpdateApplicantActionProps extends React.AriaAttributes {
  applicant: Applicant,
  onSuccess?: (applicant: Applicant) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

const UpdateApplicantAction: React.FC<UpdateApplicantActionProps> = ({
  applicant,
  onSuccess,
  children,
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);

  const modal = useModal();
  const { addSuccess, addError } = useNotifications();

  const handleSubmit = React.useCallback(async (values: UpdateApplicantFormModel) => {
    setIsSaving(true);

    try {
      const applicantToUpdate = updateApplicantFormModalToApplicant(values, applicant);

      const updatedApplicant = await updateApplicant(applicant.id!, applicantToUpdate);

      setIsSaving(false);
      addSuccess(APPLICANT_UPDATE_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(updatedApplicant);
    } catch {
      setIsSaving(false);
      addError(APPLICANT_UPDATE_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, applicant, modal, onSuccess]);

  return (
    <>
      {children
        ? children({
          isSaving,
          openUpdateModal: modal.open,
        }) : (
          <Action
            aria-label="Azione per modificare il candidato"
            label="Modifica"
            onClick={() => modal.open()}
            loading={isSaving}
            size="S"
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title="Modifica candidato"
          aria-label="Modale per modificare il candidato"
          footer={(
            <Action
              label="Modifica"
              color="primary"
              emphasis="high"
              aria-label="Modifica il candidato"
              onClick={() => formRef.current.handleSubmit()}
            />
          )}
        >
          <UpdateApplicantForm
            initialValues={applicantToUpdateApplicantFormModel(applicant)}
            innerRef={formRef}
            onSubmit={handleSubmit}
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpdateApplicantAction;
